import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcB = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990/990EZ Schedule B: Nonprofit Contribution Reporting"
          description="Effortlessly report contributions with Form 990/990EZ Schedule B. Ensure nonprofit transparency and IRS compliance. Simplify financial disclosure now!"
          keywords="Form 990-EZ Schedule B, Form 990 Schedule B, Nonprofit tax filing, Schedule of Contributors, Contribution reporting"
          canonical="resources/tax-exempt/form-990-schedule-b/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule B</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report Schedule of Contributors with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schb`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-b/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule B at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 990 Schedule B{' '}
                        </strong>{' '}
                        reports contributors and reportable contributions
                        received during the tax year. It is attached to{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990,
                        </Link>{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-ez/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-EZ
                        </Link>{' '}
                        or Form 990-PF.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Organizations meeting the IRS contributor reporting
                        requirements must attach{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule B.</strong>{' '}
                        For many organizations, this generally includes{' '}
                        <strong style={{ fontWeight: 600 }}>
                          contributions of $5,000 or more from a single
                          contributor
                        </strong>
                        , subject to IRS rules.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Reportable{' '}
                        <strong style={{ fontWeight: 600 }}>
                          cash contributions, noncash gifts, grants, bequests,
                          devises,
                        </strong>{' '}
                        and other qualifying donations.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Contributor Information:
                        </strong>{' '}
                        Depending on the organization's tax-exempt
                        classification, contributor details may be required or
                        subject to limited public disclosure under IRS rules.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule B?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule B?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schb" sx={{ color: '#0202ff' }}>
                          Schedule B filing Requirements
                        </Link>
                        <List>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              {' '}
                              <Link href="#contri" sx={{ color: '#0202ff' }}>
                                Part I - Contributors
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#nocash" sx={{ color: '#0202ff' }}>
                                Part II - NonCash Property
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#exclusi" sx={{ color: '#0202ff' }}>
                                Part III - Exclusively religious, charitable,
                                etc., contributions to organizations described
                                in section 501(c)(7), (8), or (10) that total
                                more than $1,000 for the year from a single
                                contributor
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule B filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule B?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>Schedule B, </strong>
                  <i>Schedule of Contributors </i> is a supplementary schedule
                  filed annually by{' '}
                  <strong style={{ fontWeight: 600 }}>
                    tax-exempt organizations ({' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990
                    </Link>
                    /{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      990-EZ
                    </Link>
                    ){' '}
                  </strong>{' '}
                  and{' '}
                  <strong style={{ fontWeight: 600 }}>
                    private foundations (
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-pf/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990-PF
                    </Link>
                    ){' '}
                  </strong>{' '}
                  for reporting significant contributions received by the
                  organization during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 5 }}>
                  It contains detailed information about donors who contribute
                  <span style={{ fontWeight: 600 }}> $5,000 or more</span>{' '}
                  during the reporting period, including their names, addresses,
                  and contribution amounts and to ensure proper reporting,
                  organizations should carefully review the Form 990 Schedule B
                  instructions before filing.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule B?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Not all tax-exempt organizations are mandated to file IRS
                  Schedule B. However, those falling under{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    section 501(c)(3)
                  </Link>{' '}
                  of the Internal Revenue Code, encompassing charitable,
                  educational, and religious organizations, must adhere to this
                  requirement.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your exempt organization or private foundation receives{' '}
                  <strong style={{ fontWeight: 600 }}>
                    substantial contributions meeting or exceeding the $5,000
                  </strong>{' '}
                  threshold from a single donor, Schedule B becomes a necessary
                  filing obligation. You must be attached along with your
                  information return.
                </Typography>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Complete your Schedule B filing requirements and e-file your{' '}
                    annual information return with TaxZerone. It&apos;s simple
                    and easy!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schb`}
                  >
                    Get Started With TaxZerone
                  </Button>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schb"
                >
                  Schedule B filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schb-organ-img.webp" />
                  <source srcSet="/assets/images/f990-schb-organ-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schb-organ-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule B showing organization type information"
                    width={760}
                    height={392}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  All Section 501(c)(3) and Section 527 organizations that file{' '}
                  Form 990/990-EZ or Form 990-PF must complete and attach All
                  Section{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    501(c)(3)
                  </Link>{' '}
                  and Section 527 organizations that file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990
                  </Link>
                  /{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ{' '}
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-PF
                  </Link>{' '}
                  must complete and attach{' '}
                  <span style={{ fontWeight: 600 }}> Schedule B</span> along
                  with their information return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1.5,
                    lineHeight: 1.5,
                  }}
                >
                  Organization Information
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Name of the Organization
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your organization’s full legal name exactly as it
                  appears on your Form 990, 990-EZ, or <br />
                  <Link
                    href="/resources/tax-exempt/form-990-pf-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-PF
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Organization Type
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You’ll need to check the box that matches your organization
                  type:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Form 990 or 990-EZ
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    501(c) ( ) Organization - Enter the correct subsection
                    number (for example, 501(c)(3), 501(c)(4), etc.).{' '}
                  </li>
                  <li>
                    4947(a)(1) Nonexempt Charitable Trust (Not Treated as a
                    Private Foundation){' '}
                  </li>
                  <li>527 Political Organization </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Form 990-PF
                </Typography>
                <Box component="ul" sx={{ mb: 1.2, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="/resources/tax-exempt/501c3-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      501(c)(3)
                    </Link>{' '}
                    Exempt Private Foundation (Form 990-PF Filers)
                  </li>
                  <li>
                    4947(a)(1) Nonexempt Charitable Trust (Treated as a Private
                    Foundation)
                  </li>
                  <li>501(c)(3) Taxable Private Foundation </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f990-schb-list-img.webp" />
                  <source srcSet="/assets/images/f990-schb-list-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schb-list-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule B listing contributors under the general rule or special rule for reporting by the organization."
                    width={760}
                    height={508}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This part of Schedule B helps you figure out whether your
                  organization is required to report certain contributors. The
                  filing requirement is based on how much your organization
                  received from individual donors during the year and the type
                  of exempt organization you are.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  General Rule
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For most organizations that file Form 990, 990-EZ, or 990-PF,
                  Schedule B is required if a single donor gave $5,000 or more
                  during the year, whether the contribution was cash or
                  property. When that happens, you’ll need to complete Parts I
                  and II of Schedule B. Be sure to total all donations from the
                  same person or entity before deciding whether the $5,000 limit
                  has been reached.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Special Situations
                </Typography>
                <Typography variant="body1">
                  Some organizations follow different limits.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you are a{' '}
                    <Link
                      href="https://blog.taxzerone.com/posts/what-is-501c3-a-clear-guide-to-nonprofit-tax-exempt-status/"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      501(c)(3)
                    </Link>{' '}
                    public charity that qualifies under the public support test,
                    Schedule B is required when one contributor gives more than
                    $5,000 or more than 2% of your total annual contributions
                    (whichever is higher). In this case, you complete Parts I
                    and II.
                  </li>
                  <li>
                    If your organization is classified under 501(c)(7),
                    501(c)(8), or 501(c)(10), you must file Schedule B if any
                    one contributor gave more than $1,000 strictly for
                    religious, charitable, educational, scientific, literary, or
                    similar purposes. When this applies, complete Part I (enter
                    “N/A” instead of the contributor’s name), Part II, and Part
                    III.{' '}
                  </li>
                  <li>
                    If no individual donor gave more than $1,000 for those
                    specific purposes, you only need to report the total amount
                    received for those purposes. You do not complete the
                    remaining parts unless the general $5,000 rule also applies.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  When It’s Not Required
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization does not meet any of these thresholds,
                  Schedule B is not required. However, you must confirm this on
                  your main Form 990, 990-EZ, or 990-PF by indicating that
                  Schedule B does not apply.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                    scrollMarginTop: 83,
                  }}
                  id="contri"
                >
                  Part I - Contributors
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schb-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schb-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schb-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule B Part I Contributors"
                    width={686}
                    height={809}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To complete this section, organizations should provide details
                  about the contributors who contributed more than $5000 in the
                  tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List each contributor in order. Start with{' '}
                  <span style={{ fontWeight: 600 }}> No. 1</span> for the first
                  donor, <span style={{ fontWeight: 600 }}>No. 2</span> for the
                  second, and continue numbering consecutively.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Section 501(c)(3) and Section 527 organizations must enter
                    the contributor’s{' '}
                    <span style={{ fontWeight: 600 }}>
                      name, address, and ZIP code
                    </span>
                    .
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If the donor’s identity is truly unknown, enter
                    <span style={{ fontWeight: 600 }}>“Anonymous”</span>.{' '}
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If your organization type is not required to disclose donor
                    details, enter{' '}
                    <span style={{ fontWeight: 600 }}>“N/A.”</span>
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>total amount</span> the
                  contributor gave during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Check the box that applies:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Person –</span> Direct
                    cash donations (cash, check, credit card, wire transfer,
                    etc.).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Payroll –</span> Donations
                    given through an employer’s payroll deduction program.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Noncash –</span> Donations
                    of property (such as equipment, supplies, or other
                    goods).{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                    scrollMarginTop: 83,
                  }}
                  id="nocash"
                >
                  Part II - NonCash Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schb-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schb-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schb-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule B Part II showing noncash property contributions"
                    width={686}
                    height={807}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization reported any NonCash type of contributions
                  in Part I (Contributors), they should provide more details
                  about those non-cash contributions in this part.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The details include:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Description of noncash property, which are the details about
                    the property received from the contributor.
                  </li>
                  <li style={{ marginBottom: 16 }}>Fair Market Value (FMV)</li>
                  <li style={{ marginBottom: 16 }}>
                    Date received - the date on which the property was received
                    from the contributor.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                    scrollMarginTop: 83,
                  }}
                  id="exclusi"
                >
                  Part III - Exclusively religious, charitable, etc.,
                  contributions to organizations described in section 501(c)(7),
                  (8), or (10) that total more than $1,000 for the year from a
                  single contributor
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schb-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schb-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schb-part3-img.png"
                    loading="lazy"
                    alt="IRS Form 990 Schedule B Part III exclusively religious and charitable contributions to section 501(c)(7) and 501(c)(8) organizations "
                    width={688}
                    height={822}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Only Section 501(c)(7), (8), or (10) organizations that
                  received contributions (gifts) exclusively for religious,
                  charitable, etc., purposes should report that information in
                  this part.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the contributor number that matches the same donor
                  listed in Part I.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Briefly state why the donation was given (for example, for
                  religious, charitable, educational, or similar purposes).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Explain how the organization actually used or plans to use the
                  donated amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Describe how the funds are being held, for example, kept in a
                  separate account or combined with other organizational funds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the donation was transferred to another organization,
                  provide the transferee’s name, address, ZIP+4, and explain the
                  relationship between your organization (transferor) and the
                  receiving organization.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule B filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>TaxZerone</strong> is an
                  IRS-authorized e-file service provider, offering more than
                  just a seamless filing experience.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  By filing with us, you get instant updates on your{' '}
                  {/* <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ filing
                  </Link>{' '} */}
                  return&apos;s filing status, ensuring you&apos;re always in
                  the loop. <br />
                  At <strong style={{ fontWeight: 600 }}>TaxZerone</strong>,
                  we&apos;ve designed a user-friendly platform that not only
                  simplifies the e-filing process but also provides
                  comprehensive support at every step.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s how your Form 990/990 EZ or Form 990-PF return
                  with Schedule B attachment is transmitted to the IRS in 3
                  simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization/Foundation Details -
                    </strong>{' '}
                    Choose the tax year for which you want to file a return, and
                    provide the required details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview the Return -
                    </strong>{' '}
                    Complete Schedule B and preview the information provided in
                    the return for accuracy before transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS -
                    </strong>{' '}
                    Transmit Schedule B along with your Form 990/990 EZ or Form
                    990-PF return to the IRS and get the acceptance in just a
                    few hours.
                  </li>
                </ol>
                <Typography variant="body1" id="choose" sx={{ mb: 2 }}>
                  In case of IRS rejection for your information return, worry
                  not! TaxZerone has your back. We offer a hassle-free
                  correction and retransmission process, absolutely free of
                  charge!
                </Typography>

                {/* <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mb: 5 }}
                  
                >
                  <strong style={{fontWeight:600}} >Note:</strong> Don&apos;t include in Part VI the names of any
                  donors, grantors, or contributors because Part VI will be made
                  available for public inspection.
                </Alert> */}

                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.3 }}
                      >
                        Choose TaxZerone for a filing experience that goes
                        beyond expectations.
                      </Typography>
                      <Typography variant="body2">
                        Empower your tax compliance journey with us today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schb`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          <Link
                            href="https://www.irs.gov/instructions/i990sb"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule B – Schedule of Contributors
                        </li>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990ez.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990-EZ – Short Form Return of Organization
                          Exempt From Income Tax
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/instructions/i990pf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990-PF – Return of Private Foundation
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. Why is Schedule B important?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" sx={{ mt: 0.5 }} itemProp="text">
                    <span style={{ fontWeight: 600 }}> Schedule B</span> plays a
                    crucial role in maintaining transparency and accountability.
                    It provides the IRS and the public with detailed information
                    about significant contributions, fostering trust and
                    integrity within the nonprofit sector.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What information must be provided for each donor reported
                  on Schedule B?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mt: 1 }}>
                      For each donor contributing{' '}
                      <span style={{ fontWeight: 600 }}> $5,000 or more</span>,
                      organizations must disclose the following information.
                    </Typography>
                    <ul>
                      <li>Name and address of the contributor</li>
                      <li>Total contribution</li>
                      <li>Type of contribution</li>
                    </ul>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Can Schedule B be made available for Public Inspection?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mt: 0.5, mb: 2 }}>
                      Yes. Schedule B is open to public inspection for an
                      organization that files Form 990-PF and for a Section 527
                      political organization that files Form 990 or 990-EZ .
                    </Typography>
                    <Typography>
                      However, don&apos;t include the social security numbers of
                      contributors as this information may be made public.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcB;
